'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@/hen-ui/utils';
import { usePortalContainer } from '@/hen-ui/providers/PortalContainerContext';

const TooltipProvider = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Provider>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Provider>
>(({ delayDuration = 0, ...props }, _ref) => (
  <TooltipPrimitive.Provider
    data-slot='tooltip-provider'
    delayDuration={delayDuration}
    {...props}
  />
));

TooltipProvider.displayName = TooltipPrimitive.Provider.displayName;

const Tooltip = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Root>
>(({ ...props }, _ref) => (
  <TooltipPrimitive.Root data-slot='tooltip' {...props} />
));

Tooltip.displayName = TooltipPrimitive.Root.displayName;

const TooltipTrigger = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Trigger>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ ...props }, ref) => (
  <TooltipPrimitive.Trigger data-slot='tooltip-trigger' ref={ref} {...props} />
));

TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
  React.ElementRef<typeof TooltipPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 0, children, ...props }, ref) => {
  const portalContainer = usePortalContainer();
  return (
    <TooltipPrimitive.Portal container={portalContainer}>
      <TooltipPrimitive.Content
        ref={ref}
        data-slot='tooltip-content'
        sideOffset={sideOffset}
        className={cn(
          // Animation
          'data-[state=open]:animate-fade-in-0',
          'data-[state=open]:animate-zoom-in-95',
          'data-[state=delayed-open]:animate-fade-in-0',
          'data-[state=delayed-open]:animate-zoom-in-95',
          'data-[state=closed]:animate-fade-out-0',
          'data-[state=closed]:animate-zoom-out-95',
          'data-[side=bottom]:animate-slide-in-from-top',
          'data-[side=left]:animate-slide-in-from-right',
          'data-[side=right]:animate-slide-in-from-left',
          'data-[side=top]:animate-slide-in-from-bottom',
          // Content styling
          'rounded-sm px-3 py-1.5 text-xs bg-neutral-black text-neutral-white z-popover w-fit max-w-xs origin-[--radix-tooltip-content-transform-origin]',
          className,
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className='size-2.5 rotate-45 rounded-xxs bg-neutral-black fill-neutral-black z-popover translate-y-[calc(-50%_-_2px)]' />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
});

TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger };
